import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Pantry() {
  const { user } = useAuth();
  const [step, setStep] = useState("scan"); // scan → confirm → result
  const [scanning, setScanning] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [newIng, setNewIng] = useState("");
  const [people, setPeople] = useState(2);
  const [mealType, setMealType] = useState("dinner");
  const [maxTime, setMaxTime] = useState(0);
  const [meals, setMeals] = useState([]);
  const [saving, setSaving] = useState({});
  const [addingList, setAddingList] = useState({});
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("tesco");
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: storeList }, { data: prefs }] = await Promise.all([
          api.get("/stores"),
          api.get("/me/preferences"),
        ]);
        setStores(storeList || []);
        if (prefs?.preferred_store_id) setStoreId(prefs.preferred_store_id);
      } catch {
        // non-blocking — Pantry still works without the picker
      }
    })();
  }, []);

  const currentStoreName = stores.find((s) => s.id === storeId)?.name || "your store";

  const changeStore = async (newId) => {
    setStoreId(newId);
    try {
      await api.put("/me/preferred-store-id", { store_id: newId });
    } catch {
      // silent — UI already reflects the change locally
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setScanning(true);
      try {
        const { data } = await api.post("/pantry/scan", { image_base64: reader.result });
        if (data.non_food_visible || !data.ingredients?.length) {
          toast.error("No food spotted — try a clearer angle or add ingredients manually below.");
          setIngredients([]);
        } else {
          setIngredients(data.ingredients);
          toast.success(`Spotted ${data.ingredients.length} ingredient${data.ingredients.length > 1 ? "s" : ""}`);
        }
        setStep("confirm");
      } catch (err) {
        toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Scan failed");
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const skipScan = () => {
    setIngredients([]);
    setStep("confirm");
  };

  const removeIng = (idx) => setIngredients((cur) => cur.filter((_, i) => i !== idx));
  const addIng = () => {
    const v = newIng.trim();
    if (!v) return;
    setIngredients((cur) => cur.includes(v) ? cur : [...cur, v]);
    setNewIng("");
  };

  const suggest = async () => {
    if (!ingredients.length) {
      toast.error("Add at least one ingredient first.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/pantry/suggest-meals", {
        ingredients,
        people: Number(people),
        meal_type: mealType,
        max_cook_minutes: Number(maxTime) || null,
      });
      setMeals(data.meals || []);
      setStep("result");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't build meals."));
    } finally {
      setSuggesting(false);
    }
  };

  const saveMeal = async (m, idx) => {
    setSaving((cur) => ({ ...cur, [idx]: true }));
    try {
      await api.post("/pantry/save-recipe", {
        title: m.title, servings: m.servings, kcal_per_serving: m.kcal_per_serving,
        needed: m.needed, steps: m.steps, tags: m.tags, image: m.image, meal_type: m.meal_type,
      });
      toast.success(`"${m.title}" saved to your Recipes`);
    } catch {
      toast.error("Couldn't save that one.");
    } finally {
      setSaving((cur) => ({ ...cur, [idx]: false }));
    }
  };

  const addMissingToList = async (m, idx) => {
    const missing = (m.missing || []).filter((x) => x && x.trim());
    if (!missing.length) return;
    setAddingList((cur) => ({ ...cur, [`missing-${idx}`]: true }));
    try {
      const { data } = await api.post("/shopping-lists/add-meal", {
        meal: {
          title: `Missing for ${m.title}`,
          image: m.image || null,
          servings: m.servings || people,
          ingredients: missing.map((name) => ({ name, quantity: null, notes: null })),
          tags: ["pantry-scan", "missing-items", ...(m.tags || [])],
          calories_per_serving: null,
        },
      });
      const verb = data.created ? "Created basket" : "Added to";
      toast.success(`${verb} "${data.list.name}" · +${data.added_count} item${data.added_count === 1 ? "" : "s"} · Ready to shop at ${currentStoreName}`, {
        duration: 7000,
        action: {
          label: `Shop at ${currentStoreName}`,
          onClick: () => { window.location.href = `/app/shopping/${data.list.list_id}`; },
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't add to shopping list."));
    } finally {
      setAddingList((cur) => ({ ...cur, [`missing-${idx}`]: false }));
    }
  };

  const addAllToList = async (m, idx) => {
    // All ingredients (what they have + what's missing) — so a family member at the shops
    // has the complete list, not just the extras.
    const all = [
      ...((m.needed || []).map((n) => ({ name: n, notes: "already at home" }))),
      ...((m.missing || []).map((n) => ({ name: n, notes: "needs buying" }))),
    ].filter((x) => x.name && x.name.trim());
    if (!all.length) return;
    setAddingList((cur) => ({ ...cur, [`all-${idx}`]: true }));
    try {
      const { data } = await api.post("/shopping-lists/add-meal", {
        meal: {
          title: m.title,
          image: m.image || null,
          servings: m.servings || people,
          ingredients: all.map((it) => ({ name: it.name, quantity: null, notes: it.notes })),
          tags: ["pantry-scan", "full-recipe", ...(m.tags || [])],
          calories_per_serving: m.kcal_per_serving || null,
        },
      });
      const verb = data.created ? "Created basket" : "Added to";
      toast.success(`${verb} "${data.list.name}" · +${data.added_count} item${data.added_count === 1 ? "" : "s"} · Share with family from the list page`, {
        duration: 7000,
        action: {
          label: `Open & share`,
          onClick: () => { window.location.href = `/app/shopping/${data.list.list_id}`; },
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't add to shopping list."));
    } finally {
      setAddingList((cur) => ({ ...cur, [`all-${idx}`]: false }));
    }
  };

  return (
    <div className="space-y-6 ff-fade-in max-w-4xl mx-auto">
      <header>
        <div className="ff-label text-[#3B5A3E]">Cook with what you have</div>
        <h1 className="text-3xl md:text-4xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          📸 Scan fridge
        </h1>
        <p className="text-sm text-[#5C6B61] mt-1.5">
          Snap your fridge, freezer, or cupboard — we&apos;ll spot what you have and cook something tasty for the family.
        </p>
      </header>

      {step === "scan" && (
        <div className="ff-card p-8 text-center" data-testid="pantry-scan-step">
          <div className="text-6xl mb-3">📸</div>
          <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Take a photo</h3>
          <p className="text-sm text-[#5C6B61] mb-5 max-w-md mx-auto">
            Open the fridge or cupboard and snap what&apos;s inside. AI will list the ingredients — you can tweak them before we suggest meals.
          </p>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="pantry-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={scanning}
            className="ff-btn-primary disabled:opacity-50"
            data-testid="pantry-open-camera"
          >
            {scanning ? "Reading photo…" : "📷 Open camera / choose photo"}
          </button>
          <div className="mt-4">
            <button
              onClick={skipScan}
              className="text-sm text-[#3B5A3E] underline-offset-4 hover:underline"
              data-testid="pantry-skip-scan"
            >
              or type ingredients manually →
            </button>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div className="ff-card p-6 space-y-5" data-testid="pantry-confirm-step">
          <div>
            <div className="ff-label">Ingredients spotted</div>
            <div className="mt-2 flex flex-wrap gap-2 min-h-[3rem]" data-testid="pantry-ingredients">
              {ingredients.length === 0 && (
                <p className="text-sm text-[#8B9690]">No ingredients yet — add some below.</p>
              )}
              {ingredients.map((ing, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 bg-[#E8F0E9] text-[#3B5A3E] text-sm font-medium px-3 py-1.5 rounded-full">
                  {ing}
                  <button onClick={() => removeIng(i)} className="opacity-60 hover:opacity-100" aria-label="Remove">✕</button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input type="text" value={newIng} onChange={(e) => setNewIng(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addIng()}
                placeholder="e.g. eggs, cheddar, tomatoes"
                className="ff-input flex-1"
                data-testid="pantry-add-input" />
              <button onClick={addIng} className="ff-btn-ghost" data-testid="pantry-add-btn">+ Add</button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="ff-label block mb-1.5">Serving</label>
              <select className="ff-input" value={people} onChange={(e) => setPeople(e.target.value)} data-testid="pantry-people-select">
                {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
              </select>
            </div>
            <div>
              <label className="ff-label block mb-1.5">Meal</label>
              <select className="ff-input" value={mealType} onChange={(e) => setMealType(e.target.value)} data-testid="pantry-meal-type">
                <option value="breakfast">🌅 Breakfast</option>
                <option value="lunch">🌞 Lunch</option>
                <option value="dinner">🌙 Dinner</option>
                <option value="snack">🍿 Snack</option>
              </select>
            </div>
            <div>
              <label className="ff-label block mb-1.5">Max time</label>
              <select className="ff-input" value={maxTime} onChange={(e) => setMaxTime(e.target.value)} data-testid="pantry-max-time">
                <option value="0">Any</option>
                <option value="15">Under 15 min</option>
                <option value="30">Under 30 min</option>
                <option value="45">Under 45 min</option>
                <option value="60">Under 1 hr</option>
              </select>
            </div>
            <div>
              <label className="ff-label block mb-1.5">Shop at</label>
              <select
                className="ff-input"
                value={storeId}
                onChange={(e) => changeStore(e.target.value)}
                data-testid="pantry-store-picker"
              >
                {stores.length === 0 && <option value="tesco">Tesco</option>}
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setStep("scan")} className="ff-btn-ghost" data-testid="pantry-back">Back</button>
            <button onClick={suggest} disabled={suggesting || !ingredients.length}
              className="ff-btn-primary disabled:opacity-50"
              data-testid="pantry-suggest-btn">
              {suggesting ? "Cooking up ideas… (5–10s)" : "✨ Suggest meals"}
            </button>
          </div>
        </div>
      )}

      {step === "result" && (
        <div className="space-y-4" data-testid="pantry-result-step">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
              🍽️ {meals.length} meal{meals.length !== 1 ? "s" : ""} for {people} {people === 1 ? "person" : "people"}
            </h3>
            <button onClick={() => setStep("confirm")} className="text-sm text-[#3B5A3E] font-medium hover:underline" data-testid="pantry-try-again">
              ← Change ingredients
            </button>
          </div>
          <div className="ff-card px-4 py-3 flex flex-col gap-2" data-testid="pantry-result-store-bar">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs uppercase tracking-wide text-[#8B9690] font-medium">🛒 Shop missing at</span>
              <select
                className="ff-input py-1.5 text-sm flex-1 min-w-[140px] max-w-[220px]"
                value={storeId}
                onChange={(e) => changeStore(e.target.value)}
                data-testid="pantry-store-picker-result"
              >
                {stores.length === 0 && <option value="tesco">Tesco</option>}
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-[#5C6B61] leading-relaxed">
              We&apos;ll send any missing items to <strong className="text-[#2A332C]">{currentStoreName}</strong> for click &amp; collect or delivery.
            </p>
          </div>
          {meals.map((m, i) => (
            <div key={i} className="ff-card overflow-hidden" data-testid={`pantry-meal-${i}`}>
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-56 h-48 sm:h-auto bg-[#F3F0EA] flex-none">
                  {m.image && <img src={m.image} alt={m.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5 flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{m.title}</h4>
                    <span className="ff-chip bg-[#E8F0E9] text-[#3B5A3E] text-xs">{m.kcal_per_serving} kcal</span>
                  </div>
                  <p className="text-xs text-[#5C6B61] mt-1">
                    ⏱️ {m.cook_minutes} min · Serves {m.servings}
                  </p>
                  <div className="mt-3 space-y-2 text-sm">
                    <div>
                      <span className="ff-label">You have</span>
                      <p className="text-[#2A332C]">{m.needed.join(" · ")}</p>
                    </div>
                    {m.missing.length > 0 && (
                      <div>
                        <span className="ff-label text-[#A65541]">You&apos;ll need to buy</span>
                        <p className="text-[#A65541]">{m.missing.join(" · ")}</p>
                        <p className="text-xs text-[#8B9690] mt-1">
                          Recommended — send these to Tesco, Sainsbury&apos;s, ASDA, Morrisons, Waitrose, Aldi, Lidl, Co-op, M&amp;S or Iceland for click &amp; collect / delivery.
                        </p>
                      </div>
                    )}
                    {m.missing.length === 0 && (
                      <p className="text-xs text-[#3B5A3E] font-medium">✓ You&apos;ve got everything you need</p>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {m.missing.length > 0 && (
                      <button
                        onClick={() => addMissingToList(m, i)}
                        disabled={!!addingList[`missing-${i}`]}
                        className="text-xs font-semibold bg-[#C36952] text-white px-3 py-1.5 rounded-full disabled:opacity-50 hover:bg-[#a95742] transition"
                        data-testid={`pantry-add-missing-${i}`}
                      >
                        {addingList[`missing-${i}`] ? "Adding…" : `🛒 Add missing (${m.missing.length}) to basket`}
                      </button>
                    )}
                    <button
                      onClick={() => addAllToList(m, i)}
                      disabled={!!addingList[`all-${i}`]}
                      className="text-xs font-semibold bg-[#3B5A3E] text-white px-3 py-1.5 rounded-full disabled:opacity-50 hover:bg-[#2c4630] transition"
                      data-testid={`pantry-add-all-${i}`}
                    >
                      {addingList[`all-${i}`] ? "Adding…" : "🧺 Add all to list & share"}
                    </button>
                    <button
                      onClick={() => saveMeal(m, i)}
                      disabled={saving[i]}
                      className="text-xs font-semibold bg-white border border-[#3B5A3E] text-[#3B5A3E] px-3 py-1.5 rounded-full disabled:opacity-50 hover:bg-[#F3F0EA] transition"
                      data-testid={`pantry-save-meal-${i}`}
                    >
                      {saving[i] ? "Saving…" : "💾 Save to Recipes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
